import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useAiReadyFramework, useAiReadyList, useAiReadyDetail, useCreateAiReady, useUpdateAiReady,
  useSetAiReadyStatus, useAiReadySummary, useDeleteAiReady,
  STATUS_LABEL, SEVERITY_LABEL,
  type Framework, type AssessmentInput, type Assessment, type AssessmentSummary,
  type DimensionResult, type AssessmentResult,
} from "../lib/aiReadyCheck";
import "../styles.w3.css";
import "../styles.w3b.css";

/**
 * AI-Ready-Check / Reifegrad-Assessment (CompanyBrain Welle 3b, BEN-107).
 * Strukturiertes Assessment ueber 6 Dimensionen (Skala 0-4) -> Reifegrad-Profil (Ampel/Bar),
 * Empfehlungen inkl. passender Intervention (verbatim aus Gold) + naechste Schritte.
 * Persistent (tenant+user-scoped), wieder aufrufbar & vergleichbar. Optional lokale KI-Verdichtung.
 */

type View = "list" | "run" | "result";
type Answers = Record<string, Record<string, { score: number | null; note: string }>>;

function emptyAnswers(fw?: Framework): Answers {
  const a: Answers = {};
  for (const d of fw?.dimensions ?? []) {
    a[d.key] = {};
    for (const q of d.questions) a[d.key][q.id] = { score: null, note: "" };
  }
  return a;
}

function answersFrom(assessment: Assessment, fw?: Framework): Answers {
  const a = emptyAnswers(fw);
  for (const d of fw?.dimensions ?? []) {
    const raw = (assessment.answers ?? {})[d.key] ?? {};
    for (const q of d.questions) {
      const e = (raw as Record<string, unknown>)[q.id];
      if (e && typeof e === "object") {
        const obj = e as { score?: number | null; note?: string };
        a[d.key][q.id] = { score: obj.score ?? null, note: obj.note ?? "" };
      } else if (typeof e === "number") {
        a[d.key][q.id] = { score: e, note: "" };
      }
    }
  }
  return a;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function deltaStr(a: number | null | undefined, b: number | null | undefined): string {
  if (a == null || b == null) return "—";
  const d = Math.round((b - a) * 10) / 10;
  return d > 0 ? `+${d}` : `${d}`;
}

function ScorePicker({ value, onPick }: { value: number | null; onPick: (v: number) => void }) {
  return (
    <div className="arc-scale" role="group" aria-label="Bewertung 0 bis 4">
      {[0, 1, 2, 3, 4].map((n) => (
        <button key={n} type="button" className={`arc-score ${value === n ? "active" : ""}`}
          aria-pressed={value === n} onClick={() => onPick(n)}>{n}</button>
      ))}
    </div>
  );
}

function DimBar({ d }: { d: DimensionResult }) {
  const w = d.score == null ? 0 : Math.max(0, Math.min(100, d.score));
  return (
    <div className="arc-dimbar">
      <div className="arc-dimbar-top">
        <span className={`kc-dot kc-${d.ampel} kc-dot-sm`} aria-hidden />
        <span className="arc-dim-name">{d.name}</span>
        <strong>{d.score != null ? `${d.score}` : "—"}</strong>
      </div>
      <div className="kc-kpi-bar"><span className={`kc-fill kc-${d.ampel}`} style={{ width: `${w}%` }} /></div>
    </div>
  );
}

function ResultProfile({ result, aiSummary }: { result: AssessmentResult; aiSummary?: string }) {
  const ov = result.overall_score;
  return (
    <div className="arc-result">
      <div className={`arc-overall kc-border-${result.overall_ampel}`}>
        <div className="arc-overall-score"><strong>{ov != null ? ov : "—"}</strong><span>/ 100</span></div>
        <div className="arc-overall-copy">
          <span className="kc-program-k">Gesamt-Reifegrad</span>
          <strong>Stufe {result.level?.stufe} · {result.level?.name}</strong>
          <span className="muted small">{result.level?.beschreibung}</span>
        </div>
      </div>

      <div className="arc-dims">{result.dimensions.map((d) => <DimBar key={d.key} d={d} />)}</div>

      {aiSummary && (<div className="w3-summary"><h4>KI-Einschaetzung (lokal, grounded)</h4><p>{aiSummary}</p></div>)}

      {result.recommendations.length > 0 && (
        <div className="arc-recos">
          <h3>Empfehlungen &amp; passende Interventionen</h3>
          {result.recommendations.map((r) => (
            <div key={r.dimension} className={`arc-reco sev-${r.severity}`}>
              <div className="arc-reco-head">
                <span className="w3-pill kind">{r.dimension_name}</span>
                <span className="muted small">{SEVERITY_LABEL[r.severity] ?? r.severity} · Reifegrad {r.score ?? "—"}</span>
              </div>
              <p>{r.text}</p>
              {r.intervention && (
                <Link className="w3-link-chip" to={r.intervention.route} title="Intervention verbatim aus dem Gold-Katalog">
                  Passende Intervention: {r.intervention.name} ({r.intervention.code}) →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {result.next_steps.length > 0 && (
        <div className="arc-next">
          <h3>Naechste Schritte</h3>
          <ol className="kc-list">{result.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </div>
      )}
    </div>
  );
}

export default function AiReadyCheckPage() {
  const fw = useAiReadyFramework();
  const list = useAiReadyList("all");
  const create = useCreateAiReady();
  const update = useUpdateAiReady();
  const setStatus = useSetAiReadyStatus();
  const aiSum = useAiReadySummary();
  const del = useDeleteAiReady();

  const [view, setView] = useState<View>("list");
  const [openId, setOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const [answers, setAnswers] = useState<Answers>({});
  const [current, setCurrent] = useState<Assessment | null>(null);
  const [compare, setCompare] = useState<string[]>([]);

  const detail = useAiReadyDetail(view === "result" ? openId : null);
  // Ergebnis-Datenquelle: frisch erzeugtes 'current' oder nachgeladenes Detail.
  const resultAssessment: Assessment | null =
    current && (!openId || current.id === openId) ? current : detail.data?.assessment ?? null;

  useEffect(() => {
    if (view === "result" && detail.data?.assessment && (!current || current.id !== detail.data.assessment.id)) {
      setCurrent(detail.data.assessment);
    }
  }, [detail.data, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = list.data?.items ?? [];
  const persistent = list.data?.persistent ?? false;

  function startNew() {
    setEditingId(null); setOpenId(null); setCurrent(null);
    setTitle(""); setScope(""); setAnswers(emptyAnswers(fw.data));
    aiSum.reset(); setView("run");
  }
  function editAssessment(a: Assessment) {
    setEditingId(a.id); setOpenId(a.id);
    setTitle(a.title); setScope(a.scope); setAnswers(answersFrom(a, fw.data));
    setCurrent(a); setView("run");
  }
  function openResult(id: string) {
    setCurrent(null); setOpenId(id); aiSum.reset(); setView("result");
  }
  function setScore(dim: string, q: string, score: number) {
    setAnswers((prev) => ({ ...prev, [dim]: { ...prev[dim], [q]: { ...(prev[dim]?.[q] ?? { note: "" }), score } } }));
  }
  function setNote(dim: string, q: string, note: string) {
    setAnswers((prev) => ({ ...prev, [dim]: { ...prev[dim], [q]: { ...(prev[dim]?.[q] ?? { score: null }), note } } }));
  }

  function save(status: "entwurf" | "abgeschlossen") {
    const input: AssessmentInput = {
      title: title.trim() || scope.trim() || "AI-Ready-Check", scope: scope.trim(), status, answers,
    };
    if (editingId) {
      update.mutate({ id: editingId, input }, {
        onSuccess: (res) => { setCurrent(res.assessment); setOpenId(res.assessment.id); setView("result"); },
      });
    } else {
      create.mutate(input, {
        onSuccess: (res) => { setCurrent(res.assessment); setEditingId(res.assessment.id); setOpenId(res.assessment.id); setView("result"); },
      });
    }
  }

  const answeredCount = useMemo(() => {
    let n = 0;
    for (const dim of Object.values(answers)) for (const q of Object.values(dim)) if (q.score != null) n++;
    return n;
  }, [answers]);

  const cmpItems = items.filter((i) => compare.includes(i.id));

  return (
    <section className="w3-page arc-page" aria-label="AI-Ready-Check">
      <header className="w3-hero">
        <div>
          <p className="hero-eyebrow">CompanyBrain · Welle 3b</p>
          <h1>AI-Ready-Check</h1>
          <p className="muted">
            Strukturiertes Reifegrad-Assessment: <strong>Wie AI-ready ist die Organisation bzw. das Ressort?</strong>{" "}
            Sechs Dimensionen (Daten, Governance, Use Cases, Kompetenzen, Infrastruktur, Change) — je Frage eine
            Bewertung von 0 bis 4. Ergebnis: Reifegrad-Profil, Empfehlungen mit passenden Interventionen und naechste Schritte.
          </p>
        </div>
        {view === "list"
          ? <button className="btn primary" onClick={startNew}>+ Neuer AI-Ready-Check</button>
          : <button className="btn ghost" onClick={() => { setView("list"); list.refetch(); }}>← Zur Uebersicht</button>}
      </header>

      {!persistent && view === "list" && (
        <p className="w3-warn small">Hinweis: Persistente Speicherung derzeit nicht aktiv — Eintraege wirken nur fuer diese Sitzung.</p>
      )}

      {/* LISTE */}
      {view === "list" && (
        <>
          {list.isLoading && <p className="muted">Assessments werden geladen …</p>}
          {!list.isLoading && items.length === 0 && (
            <div className="w3-empty">
              <p><strong>Noch kein AI-Ready-Check angelegt.</strong></p>
              <p className="muted small">Legen Sie den ersten Check an — fuer das Gesamtprogramm oder ein einzelnes Ressort.</p>
              <button className="btn primary" onClick={startNew}>+ Neuer AI-Ready-Check</button>
            </div>
          )}
          <div className="w3-list">
            {items.map((a: AssessmentSummary) => (
              <article key={a.id} className={`w3-card arc-card kc-border-${a.overall_ampel ?? "grau"}`}>
                <div className="w3-card-top">
                  <div className="arc-card-title" onClick={() => openResult(a.id)} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") openResult(a.id); }}>
                    <h3>{a.title || a.scope || "AI-Ready-Check"}</h3>
                    {a.scope && <span className="muted small">Bereich: {a.scope}</span>}
                  </div>
                  <div className="arc-card-score">
                    <span className={`kc-dot kc-${a.overall_ampel ?? "grau"} kc-dot-md`} aria-hidden />
                    <strong>{a.overall_score != null ? a.overall_score : "—"}</strong>
                    <span className={`w3-pill status-${a.status}`}>{STATUS_LABEL[a.status] ?? a.status}</span>
                  </div>
                </div>
                <div className="arc-mini-dims">
                  {a.dimensions.map((d) => (
                    <span key={d.key} className={`arc-mini kc-bg-${d.ampel}`} title={`${d.name}: ${d.score ?? "—"}`}>{d.score ?? "—"}</span>
                  ))}
                </div>
                <div className="w3-card-meta muted small">
                  <span>Aktualisiert {fmtDate(a.updated_at)}</span>
                  <label className="arc-cmp">
                    <input type="checkbox" checked={compare.includes(a.id)}
                      onChange={(e) => setCompare((c) => e.target.checked ? [...c, a.id].slice(-2) : c.filter((x) => x !== a.id))} />
                    vergleichen
                  </label>
                  <button className="btn ghost xsmall" onClick={() => openResult(a.id)}>Oeffnen</button>
                  <button className="btn ghost xsmall" onClick={() => { if (confirm("Diesen Check loeschen?")) del.mutate(a.id); }}>Loeschen</button>
                </div>
              </article>
            ))}
          </div>

          {cmpItems.length === 2 && (
            <div className="arc-compare">
              <h3>Vergleich</h3>
              <table className="kc-matrix arc-cmp-table">
                <thead><tr><th>Dimension</th><th>{cmpItems[0].title || cmpItems[0].scope}</th><th>{cmpItems[1].title || cmpItems[1].scope}</th><th>Δ</th></tr></thead>
                <tbody>
                  <tr className="arc-cmp-overall">
                    <th>Gesamt</th>
                    <td>{cmpItems[0].overall_score ?? "—"}</td>
                    <td>{cmpItems[1].overall_score ?? "—"}</td>
                    <td>{deltaStr(cmpItems[0].overall_score, cmpItems[1].overall_score)}</td>
                  </tr>
                  {cmpItems[0].dimensions.map((d) => {
                    const d2 = cmpItems[1].dimensions.find((x) => x.key === d.key);
                    return (
                      <tr key={d.key}>
                        <th>{d.name}</th>
                        <td>{d.score ?? "—"}</td>
                        <td>{d2?.score ?? "—"}</td>
                        <td>{deltaStr(d.score, d2?.score ?? null)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* RUN (Fragebogen) */}
      {view === "run" && fw.data && (
        <div className="arc-run">
          <div className="w3-form-grid">
            <label className="w3-field"><span>Titel</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. AI-Ready-Check Ressort 100 OPS" /></label>
            <label className="w3-field"><span>Bereich / Scope</span>
              <input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="Gesamt oder ein Ressort" /></label>
          </div>
          <p className="muted small">Bewertung je Frage: 0 = {fw.data.scale[0]?.label} … 4 = {fw.data.scale[4]?.label}. ({answeredCount} beantwortet)</p>

          {fw.data.dimensions.map((d) => (
            <div key={d.key} className="arc-dim-block">
              <div className="arc-dim-head"><h3>{d.name}</h3><span className="muted small">{d.beschreibung}</span></div>
              {d.questions.map((qq) => (
                <div key={qq.id} className="arc-q">
                  <div className="arc-q-text"><span>{qq.text}</span>{qq.hint && <em className="muted small">{qq.hint}</em>}</div>
                  <ScorePicker value={answers[d.key]?.[qq.id]?.score ?? null} onPick={(v) => setScore(d.key, qq.id, v)} />
                  <input className="arc-note" placeholder="Notiz (optional)"
                    value={answers[d.key]?.[qq.id]?.note ?? ""} onChange={(e) => setNote(d.key, qq.id, e.target.value)} />
                </div>
              ))}
            </div>
          ))}

          <div className="w3-editor-actions">
            <button className="btn primary" onClick={() => save("abgeschlossen")}
              disabled={create.isPending || update.isPending || answeredCount === 0}>
              {create.isPending || update.isPending ? "Speichere …" : "Auswerten & abschliessen"}
            </button>
            <button className="btn ghost" onClick={() => save("entwurf")}
              disabled={create.isPending || update.isPending}>Als Entwurf speichern</button>
            <button className="btn ghost" onClick={() => setView("list")}>Abbrechen</button>
          </div>
        </div>
      )}
      {view === "run" && !fw.data && <p className="muted">Fragenkatalog wird geladen …</p>}

      {/* RESULT */}
      {view === "result" && (
        <div className="arc-result-wrap">
          {detail.isLoading && !resultAssessment && <p className="muted">Ergebnis wird geladen …</p>}
          {resultAssessment && (
            <>
              <div className="arc-result-head">
                <div>
                  <h2>{resultAssessment.title || resultAssessment.scope || "AI-Ready-Check"}</h2>
                  <span className="muted small">
                    {resultAssessment.scope ? `Bereich: ${resultAssessment.scope} · ` : ""}
                    Status: {STATUS_LABEL[resultAssessment.status] ?? resultAssessment.status} · Aktualisiert {fmtDate(resultAssessment.updated_at)}
                  </span>
                </div>
                <div className="arc-result-actions">
                  <button className="btn ghost small" onClick={() => aiSum.mutate(resultAssessment.id, {
                    onSuccess: (r) => setCurrent((c) => c ? { ...c, ai_summary: r.summary } : c),
                  })} disabled={aiSum.isPending}
                    title="Lokale KI verdichtet die berechneten Ergebnisse (grounded, erfindet nichts).">
                    {aiSum.isPending ? "KI verdichtet …" : "✳ KI-Einschaetzung (lokal)"}
                  </button>
                  <button className="btn ghost small" onClick={() => editAssessment(resultAssessment)}>Bearbeiten</button>
                  <button className="btn ghost small"
                    onClick={() => setStatus.mutate({ id: resultAssessment.id, status: resultAssessment.status === "abgeschlossen" ? "entwurf" : "abgeschlossen" }, {
                      onSuccess: (r) => setCurrent(r.assessment),
                    })} disabled={setStatus.isPending}>
                    {resultAssessment.status === "abgeschlossen" ? "Wieder oeffnen" : "Abschliessen"}
                  </button>
                </div>
              </div>
              {resultAssessment.result
                ? <ResultProfile result={resultAssessment.result} aiSummary={resultAssessment.ai_summary} />
                : <p className="muted">Kein Ergebnis vorhanden.</p>}
            </>
          )}
        </div>
      )}
    </section>
  );
}
